import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { PubsubChannelType } from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { selectActiveOrg } from 'shared/store/auth-slice';
import { subscribeToChannel, unsubscribeFromChannel } from 'shared/store/realtime-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { makeLog } from 'shared/utils/log';

const log = makeLog('hooks/pub-sub');

/**
 * All subscriptions are lost when a socket disconnects. This hook
 * provides a shared implementation to maintain a subscription through
 * the socket lifecycle.
 * @param channelType
 * @param channelIdentifier
 * @param shouldSubscribe
 * @param shouldUnsubscribe
 */
export function useSocketSubscription(
  channelType: PubsubChannelType,
  channelIdentifier?: string,
  shouldSubscribe = true,
  shouldUnsubscribe = true,
) {
  const dispatch = useAppDispatch();
  const socketId = useAppSelector(s => s.realtime.sid);
  useEffect(() => {
    if (!socketId || !channelIdentifier || !shouldSubscribe) {
      // No socket id indicates we are not connected and cannot do any pubsub at this time
      return;
    }

    const p = (async () => {
      try {
        if (shouldSubscribe) {
          unwrapResult(
            await dispatch(subscribeToChannel({ channelType, channelIdentifier })),
          );
          return shouldUnsubscribe;
        } else {
          unwrapResult(
            await dispatch(unsubscribeFromChannel({ channelType, channelIdentifier })),
          );
        }
      } catch (err) {
        log.warn(
          `Failed to ${
            !shouldSubscribe ? 'un' : ''
          }subscribe to channelType=${channelType}, channelIdentifier=${channelIdentifier}`,
        );
      }
      return false;
    })();

    return () => {
      p.then(unsubscribe => {
        if (unsubscribe) {
          dispatch(unsubscribeFromChannel({ channelType, channelIdentifier }));
        }
      });
    };
  }, [
    channelType,
    channelIdentifier,
    shouldSubscribe,
    shouldUnsubscribe,
    socketId,
    dispatch,
  ]);
}

/**
 * Subscribe to a channel that has an identifier of the current user ID
 * @param channelType
 * @param shouldSubscribe
 * @param shouldUnsubscribe
 */
export function useUserSocketSubscription(
  channelType: PubsubChannelType,
  shouldSubscribe = true,
  shouldUnsubscribe = true,
) {
  const user = useAppSelector(selectActiveUser);
  const channelIdentifier = user?.id.toString();
  useSocketSubscription(
    channelType,
    channelIdentifier,
    shouldSubscribe,
    shouldUnsubscribe,
  );
}

/**
 * Subscribe to a channel that has an identifier of the current org ID
 * @param channelType
 * @param shouldSubscribe
 * @param shouldUnsubscribe
 */
export function useOrgSocketSubscription(
  channelType: PubsubChannelType,
  shouldSubscribe = true,
  shouldUnsubscribe = true,
) {
  const org = useAppSelector(selectActiveOrg);
  const channelIdentifier = org?.id.toString();
  useSocketSubscription(
    channelType,
    channelIdentifier,
    shouldSubscribe,
    shouldUnsubscribe,
  );
}
